import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData } from '@remix-run/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { SubscriberGate } from '~/components/Actions/types';
import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { Image } from '~/components/Image';
import Signup from '~/components/Signup';
import Typography from '~/components/Typography';

import type { Publication } from '~/interfaces/publication';

interface EmailCapturePopupProps {
  title: string;
  message: string;
  publication: Publication;
  cta: string;
  isOpen: boolean;
  onClose: () => void;
}

const DESKTOP_MIN_WIDTH = 768;

const EmailCapturePopup = ({ title, message, cta, publication, isOpen, onClose }: EmailCapturePopupProps) => {
  const { signupWidgetConfigs } = useRouteLoaderData<any>('root');
  const { t } = useTranslation();
  const { handleOpenModal } = useGlobalModals();

  const extendedOnClose = () => {
    const docuumentWidth = window?.innerWidth || document?.documentElement?.clientWidth || document?.body?.clientWidth;

    if (docuumentWidth >= DESKTOP_MIN_WIDTH) {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={extendedOnClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-50"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-50"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-4 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-50"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-50"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative -mt-32 w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-3 text-center align-middle shadow-xl transition-all md:p-6">
                <button aria-label="Close" className="absolute right-3 top-3 sm:right-6 sm:top-6" onClick={onClose}>
                  <XMarkIcon className="h-7 w-7 stroke-1 text-gray-500 transition-all hover:text-gray-700" />
                </button>
                <div className="m-auto h-12 w-12 overflow-hidden rounded-wt">
                  <Image
                    src={publication.logo.thumb.url}
                    alt={publication.name}
                    maxWidth={48}
                    width={48}
                    height={48}
                    aspectRatio="square"
                  />
                </div>
                <Typography
                  as="h3"
                  weight="bold"
                  fontFamilyv2={publication.web_template.web_theme.font_header}
                  className="tracking-medium mt-6 pb-3 text-2xl font-bold leading-tight text-gray-900"
                >
                  {title}
                </Typography>
                <Typography
                  as="h5"
                  fontFamilyv2={publication.web_template.web_theme.font_header}
                  className="mb-2 pb-10 text-sm font-light leading-tight tracking-wider text-gray-700"
                >
                  {message}
                </Typography>
                <Signup
                  signupStyles={{
                    ...signupWidgetConfigs,
                    buttonText: cta,
                    fontSize: 'sm',
                  }}
                  type={SubscriberGate.POPUP}
                />
                <div className="-ml-6 mt-2 w-[calc(100%+3rem)] border-t border-t-gray-200 pb-1 pt-6">
                  <Typography
                    as="span"
                    fontFamilyv2={publication.web_template.web_theme.font_header}
                    className="mb-4 pb-10 text-sm font-light leading-tight tracking-wider text-gray-700"
                  >
                    {t('login.already_a_subscriber')}
                    <button
                      type="button"
                      onClick={() => {
                        handleOpenModal('login');
                        onClose();
                      }}
                      className="ml-1"
                    >
                      <span className="text-wt-primary underline underline-offset-1">{t('shared.cta.sign_in')}</span>.
                    </button>
                  </Typography>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EmailCapturePopup;
